import {isServer} from 'coreUtils/utils';
import {MAX_CACHE_AGE_MS} from 'brandConstants';

/**
 * Represents SSRCacheMgr
 */
type SSRCacheRecord = {
	data: unknown;
	time: number;
	expirationTime?: number | null;
};

/**
 * Manages server-side rendering cache backed by process-global storage.
 */
class SSRCacheMgr {
	private map: Map<string, SSRCacheRecord> = typeof process !== 'undefined'
		? (process as any).ssrCache // eslint-disable-line no-undef
		: new Map();

	/**
	 * Sets the cache data for the given key
	 * @param {String} key - cache key
	 * @param {any} data - cache data
	 * @param {number|null} expirationTime - cache data
	 */
	set(key: string, data: any, expirationTime: number = null): void {
		if (!isServer) {
			return;
		}

		this.map.set(key, {
			data,
			time: new Date().valueOf(),
			expirationTime,
		});
	}

	/**
	 * Gets the data for the given key
	 * @param {String} key - cache key
	 */
	get(key: string): any | null {
		if (!isServer) {
			return null;
		}

		if (this.map.has(key)) {
			const record = this.map.get(key);
			const now = new Date();
			const expirationTime = record.expirationTime || MAX_CACHE_AGE_MS;

			if (record.time + expirationTime > now.valueOf()) {
				return record.data;
			}
		}

		return null;
	}

	/**
	 * Checks if the cache contains the given key
	 * @param {string} key - The cache key
	 * @returns {boolean} - True if the cache contains the key, otherwise false
	 */
	has(key: string) {
		return this.map.has(key);
	}
}

export default new SSRCacheMgr();
